import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RuleArticle } from '@proref/types';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { notifySuccess, notifyError } from '../../lib/toast';
import { useCreateRuleArticle, useUpdateRuleArticle } from '../../hooks/use-rules-admin';
import {
  RuleArticleFields,
  EMPTY_RULE,
  fromRuleArticle,
  toInput,
  isRuleValid,
  type EditableRule,
} from './RuleArticleFields';

interface RuleArticleFormModalProps {
  slug: string;
  // Present → edit that article; omitted → add a new one.
  existing?: RuleArticle;
  onClose: () => void;
}

// Add or edit a single rule article. Controlled fields (RuleArticleFields); the server's Zod
// schema is the real validation gate — a 409 (duplicate number) or 400 surfaces via notifyError.
export function RuleArticleFormModal({ slug, existing, onClose }: RuleArticleFormModalProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<EditableRule>(existing ? fromRuleArticle(existing) : EMPTY_RULE);
  const create = useCreateRuleArticle(slug);
  const update = useUpdateRuleArticle(slug);
  const busy = create.isPending || update.isPending;

  async function submit() {
    if (!isRuleValid(value)) {
      notifyError(t('admin.rulebooks.formModal.fillRequiredError'));
      return;
    }
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, input: toInput(value) });
        notifySuccess(t('admin.rulebooks.formModal.updatedToast'));
      } else {
        await create.mutateAsync(toInput(value));
        notifySuccess(t('admin.rulebooks.formModal.addedToast'));
      }
      onClose();
    } catch (err) {
      notifyError(err, t('admin.rulebooks.formModal.saveErrorToast'));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? t('admin.rulebooks.formModal.editTitle', { number: existing.article_number }) : t('admin.rulebooks.addRule')}
      sub={t('admin.rulebooks.formModal.sub')}
      footer={
        <>
          <Button variant="default" size="md" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="accent" size="md" onClick={submit} disabled={busy}>
            {busy ? <Spinner className="h-4 w-4" /> : existing ? t('admin.users.editDialog.saveChanges') : t('admin.rulebooks.addRule')}
          </Button>
        </>
      }
    >
      <RuleArticleFields value={value} onChange={setValue} idPrefix="rule-form" />
    </Modal>
  );
}
